import React, { useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';

interface MobileModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
    showCloseButton?: boolean;
    className?: string;
    /** Optional sticky footer content (e.g. submit/cancel buttons) */
    footer?: React.ReactNode;
}

const MobileModal: React.FC<MobileModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    size = 'md',
    showCloseButton = true,
    className = '',
    footer
}) => {
    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    // Handle escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) onClose();
        };
        if (isOpen) document.addEventListener('keydown', handleEscape);
        return () => { document.removeEventListener('keydown', handleEscape); };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const sizeClasses: Record<string, string> = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-2xl',
        xl: 'max-w-3xl w-full mx-4 md:mx-8',
        full: 'max-w-full w-full mx-0 md:mx-4 md:max-w-lg lg:max-w-xl xl:max-w-2xl'
    };

    return (
        <div className="mobile-modal modal-container">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-neutral-950/60 backdrop-blur-sm transition-opacity duration-200 modal-backdrop"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div
                className={`mobile-modal-content modal-content ${sizeClasses[size]} ${className} ${footer ? 'flex flex-col' : ''}`}
                style={{ contain: 'layout paint', WebkitOverflowScrolling: 'touch' }}
            >
                {/* Header */}
                {(title || showCloseButton) && (
                    <div className="sticky top-0 z-[1] flex items-center justify-between px-6 py-4 md:px-8 border-b border-monday-border-light bg-white/95 backdrop-blur rounded-t-2xl flex-shrink-0">
                        {title && (
                            <h2 className="text-lg font-bold tracking-tight text-monday-text">
                                {title}
                            </h2>
                        )}
                        {showCloseButton && (
                            <button
                                onClick={onClose}
                                className="p-2 -mr-1 rounded-xl text-monday-text-muted hover:text-monday-text hover:bg-monday-header transition-colors duration-150"
                                aria-label="Close modal"
                            >
                                {FiIcons.FiX({ size: 20 })}
                            </button>
                        )}
                    </div>
                )}

                {/* Body */}
                <div className={`px-6 py-5 md:px-8 md:py-6 ${footer ? 'flex-1 overflow-y-auto' : ''}`}>
                    {children}
                </div>

                {/* Sticky Footer */}
                {footer && (
                    <div className="sticky bottom-0 z-[1] px-6 py-4 md:px-8 border-t border-monday-border-light bg-white/95 backdrop-blur rounded-b-2xl flex items-center justify-end gap-3 flex-shrink-0">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MobileModal;
